import React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ReactComponent as GoogleSheetsIcon } from 'assets/google-sheets-white-icon.svg';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ConnectionKind } from '@mm/state/model/ConnectionModel';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import './IntegrationBaseComponent.scss';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useConnectionsTableActions } from '@mm/state/modules/connectionsTableState/actions';
import { ReactComponent as AccountIcon } from '../../../../../../../../layout/BotBuilderSidebar/UserDropdown/UserDropdownIcons/UserFallback.svg';
import { GoogleAccount } from '@mm/state/interfaces/googleSheet';
import { LinkOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';

type Props = {
  isAccountConnected: boolean;
  spreadsheetCount: number;
  accountsCount: number;
  oauthUrl: string;
  showUpgradeModal: (onSuccessBilling: () => void) => void;
  className?: string;
  name?: string;
  accounts: GoogleAccount[];
};

export const HeaderComponent = (props: Props) => {
  const {
    isAccountConnected,
    spreadsheetCount,
    accountsCount,
    oauthUrl,
    name,
    showUpgradeModal,
    className,
    accounts,
  } = props;

  const history = useHistory();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { setActiveIntegration } = useConnectionsTableActions();
  const { create } = useConnectionCRUDActions();
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const { showSlideoutMenu } = useViewActions();

  const addNewConnection = async () => {
    const connection = await create(activeFbPageId, {
      name: 'Connection',
      description: '',
      kind: ConnectionKind.WIDGET,
      connectionKind: 'google_spreadsheet',
    });

    if (connection) {
      showSlideoutMenu('googleSheetIntegrationEditor', {
        widgetId: connection.id,
      });
    }
  };

  const onClickAddNew = e => {
    e.stopPropagation();
    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(addNewConnection);
    } else {
      addNewConnection();
    }
    setActiveIntegration(name);
  };

  const onConnectAccountClick = e => {
    if (!hasNativeIntegrationsFeature) {
      e.stopPropagation();
      showUpgradeModal(() => history.push(oauthUrl));
    } else {
      window.open(oauthUrl, '_self');
    }
  };

  const headerClassname = classnames('integrations__header', className, {
    'integrations__header--account-connected': isAccountConnected,
  });

  return (
    <div className={headerClassname}>
      <div className="integrations__header-main">
        <div className="integrations__header-icon integrations__header-icon-googlesheets">
          <GoogleSheetsIcon />
        </div>
        <div>
          <div className="integrations__header-title">
            Google Sheets Integration
          </div>
          <div className="integrations__header-info">
            <div className="integrations__header-learn-more">
              {isAccountConnected
                ? 'The integration provides you with an ability to save customers data from MobileMonkey bot to Google Sheets.'
                : ' Automatically send data from MobileMonkey to Google Sheets.'}
            </div>
          </div>
        </div>
      </div>
      {!isAccountConnected && (
        <PrimaryButton
          className="integrations__header-add-button"
          onClick={onConnectAccountClick}
          color={ButtonColor.blue}
        >
          Add an Account
        </PrimaryButton>
      )}
      {isAccountConnected && (
        <div className="integrations__header-actions">
          <PrimaryToolTip
            align="center"
            position="bottom"
            anchorElement={
              <div className="integrations__header-status">
                Google Accounts
                <AccountIcon className="integrations__header-status-icon" />
                <div className="integrations__header-status-count">
                  {accountsCount}
                </div>
              </div>
            }
            useHover
          >
            Google Accounts used in сonnections
          </PrimaryToolTip>
          <PrimaryButton
            className="integrations__header-add-button-secondary"
            onClick={onConnectAccountClick}
            color={ButtonColor.secondaryBlue}
          >
            Add an Account
          </PrimaryButton>
          <div className="integrations__header-status">
            Connections
            <LinkOutlined className="integrations__header-status-icon" />
            <div className="integrations__header-status-count">
              {spreadsheetCount}
            </div>
          </div>
          <PrimaryButton
            className="integrations__header-add-button"
            onClick={onClickAddNew}
            color={ButtonColor.blue}
            disabled={!accounts.find(acc => !acc.readOnly)}
          >
            Add a Connection
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};
